import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { useFileUpload } from "../hooks/useFileUpload";
import { CATEGORIES, COLORS, ZONES, SHIPPING_OPTIONS, CATEGORY_FIELDS, formatPrice, calcCommission } from "../utils/helpers";

export default function ProductForm({ existing = null }) {
  const { addProduct, updateProduct, COMMISSION_RATE, currentUser, getMySales } = useApp();
  const navigate = useNavigate();
  const isEdit = !!existing;

  const [form, setForm] = useState({
    title: existing?.title || "",
    description: existing?.description || "",
    price: existing?.price || "",
    color: existing?.color || "",
    quantity: existing?.quantity || 1,
    category: existing?.category || CATEGORIES[0],
    zone: existing?.zone || ZONES[0],
    shipping: existing?.shipping || "no",
    shippingCost: existing?.shippingCost || "",
    shippingZoneCosts: existing?.shippingZoneCosts || {},
    colorStock: existing?.colorStock || {},
    estimatedDelivery: existing?.estimatedDelivery || "",
    isInternational: existing?.isInternational || false,
    paymentMethod: "mercadopago",
    extraFields: existing?.extraFields || {},
  });

  const [errors, setErrors] = useState({});
  const { files, addFiles, removeFile, uploading } = useFileUpload(existing?.images || []);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const setExtra = (key, val) => setForm(f => ({ ...f, extraFields: { ...f.extraFields, [key]: val } }));
  const toggleShippingZone = (zone) => setForm(f => {
    const costs = { ...f.shippingZoneCosts };
    if (zone in costs) { delete costs[zone]; } else { costs[zone] = ""; }
    return { ...f, shippingZoneCosts: costs };
  });
  const setShippingZoneCost = (zone, val) => setForm(f => ({ ...f, shippingZoneCosts: { ...f.shippingZoneCosts, [zone]: val } }));
  const setColorStock = (color, val) => setForm(f => ({ ...f, colorStock: { ...f.colorStock, [color]: val } }));
  const colorList = (color) => color ? color.split(", ").filter(Boolean) : [];
  const totalFromColorStock = (colorStock) => Object.values(colorStock).reduce((a,c) => a + (parseInt(c)||0), 0);
  const handleCategoryChange = (cat) => setForm(f => ({ ...f, category: cat, extraFields: {} }));

  const validate = () => {
    const e = {};
    if (files.length === 0) e.photos = "Subi al menos una foto del producto";
    if (!form.title.trim()) e.title = "El titulo es obligatorio";
    if (!form.price || form.price <= 0) e.price = "Ingresa un precio valido";
    if (form.paymentMethod !== "mercadopago") e.pay = "Solo se acepta Mercado Pago";
    if (form.shipping === "comprador") {
      const zonas = Object.keys(form.shippingZoneCosts);
      if (zonas.length === 0) e.shippingCost = "Elegi al menos una zona y su costo de envio";
      else if (zonas.some(z => !form.shippingZoneCosts[z] || form.shippingZoneCosts[z] <= 0)) e.shippingCost = "Falta poner el costo de envio en alguna zona";
    }
    if (colorList(form.color).length === 0) {
      e.colorStock = "Elegi al menos un color";
    } else if (colorList(form.color).length > 1 && totalFromColorStock(form.colorStock) <= 0) {
      e.colorStock = "Cargá el stock de al menos un color";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (uploading) { alert("Espera que terminen de subirse las fotos/videos"); return; }
    if (!validate()) return;

    const faltaMp    = !currentUser?.mercadopago_alias && !currentUser?.mercadopago_cbu;
    const faltaDni   = !currentUser?.dni;
    const faltaTel   = !currentUser?.phone;
    const faltaFoto  = !currentUser?.dni_photo_id;
    if (faltaMp || faltaDni || faltaTel || faltaFoto) {
      const faltantes = [
        faltaMp   && "tu alias o CBU de Mercado Pago",
        faltaDni  && "tu DNI",
        faltaTel  && "tu celular",
        faltaFoto && "la foto de tu DNI",
      ].filter(Boolean).join(", ");
      const ir = window.confirm(
        `⚠️ Te falta completar en tu perfil: ${faltantes}.\n\n` +
        "Para recibir pagos cuando vendas, necesitás completar esos datos.\n\n" +
        "¿Querés ir a tu perfil ahora para completarlo?"
      );
      if (ir) { navigate("/profile"); return; }
      alert("Necesitas completar esos datos en tu perfil antes de publicar.");
      return;
    }

    const imagesToSave = files.map(f => ({
      id: f.id, name: f.name, type: f.type,
      url: f.url, isVideo: f.isVideo || false,
    }));
    const zoneCostsParsed = Object.fromEntries(Object.entries(form.shippingZoneCosts).map(([z, c]) => [z, parseFloat(c) || 0]));
    const multiColor = colorList(form.color).length > 1;
    const colorStockParsed = Object.fromEntries(Object.entries(form.colorStock).map(([c, n]) => [c, parseInt(n) || 0]));
    const data = {
      ...form, price: parseFloat(form.price),
      shippingCost: form.shipping === "comprador" ? parseFloat(form.shippingCost) || 0 : 0,
      shippingZoneCosts: form.shipping === "comprador" ? zoneCostsParsed : {},
      colorStock: multiColor ? colorStockParsed : {},
      quantity: multiColor ? totalFromColorStock(form.colorStock) : form.quantity,
      images: imagesToSave,
    };
    try {
      if (isEdit) { await updateProduct(existing.id, data); } else { await addProduct(data); }
      navigate("/my-posts");
    } catch(e) {
      alert("No se pudo publicar: " + e.message);
    }
  };

  const priceNum = parseFloat(form.price) || 0;
  const FREE_SALES_CUTOFF = "2026-10-19T00:00:00Z";
  const paidSalesSinceCutoff = getMySales().filter(s => new Date(s.date) >= new Date(FREE_SALES_CUTOFF)).length;
  const freeSalesLeft = Math.max(0, 2 - paidSalesSinceCutoff);
  const effectiveRate = freeSalesLeft > 0 ? 0 : COMMISSION_RATE;
  const { commission, sellerReceives } = calcCommission(priceNum, effectiveRate);
  const extraFieldsDef = CATEGORY_FIELDS[form.category] || [];

  return (
    <div style={s.wrapper}>
      <h2 style={s.heading}>{isEdit ? "Editar publicacion" : "Nueva publicacion"}</h2>

      {/* Fotos y videos */}
      <div style={s.section}>
        <label style={s.label}>Fotos y Videos *</label>
        <label style={s.uploadZone}>
          <input
            type="file" multiple
            accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm"
            style={{display:"none"}}
            onChange={e => addFiles(e.target.files)}
          />
          <div style={{fontSize:32,marginBottom:6}}>📷🎥</div>
          <span style={{fontSize:13,color:"#1D9E75",fontWeight:500}}>
            {uploading ? "Subiendo archivos..." : "Tocar para agregar fotos o videos"}
          </span>
          <span style={{fontSize:11,color:"#aaa",marginTop:4}}>JPG, PNG, MP4, MOV — Videos hasta 100MB</span>
        </label>
        {errors.photos && <p style={s.err}>{errors.photos}</p>}

        {files.length > 0 && (
          <div style={s.thumbsRow}>
            {files.map(f => {
              const isVid = f.isVideo || f.type?.startsWith("video");
              return (
                <div key={f.id} style={s.thumb}>
                  {f.uploading ? (
                    <div style={s.thumbUploading}>
                      <div style={s.thumbProgress}>
                        <div style={{...s.thumbProgressFill, width:`${f.progress||0}%`}} />
                      </div>
                      <span style={{fontSize:10,color:"#fff"}}>{f.progress||0}%</span>
                    </div>
                  ) : isVid ? (
                    <div style={s.videoThumb}>
                      <video src={f.url} style={s.thumbImg} preload="metadata" />
                      <div style={s.videoIcon}>🎥</div>
                    </div>
                  ) : (
                    <img src={f.url} alt={f.name} style={s.thumbImg} />
                  )}
                  {!f.uploading && (
                    <button style={s.removeThumb} onClick={() => removeFile(f.id)}>✕</button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={s.section}>
        <label style={s.label}>Titulo *</label>
        <input style={{...s.input,...(errors.title?s.inputErr:{})}} value={form.title} onChange={e => set("title", e.target.value)} placeholder="Ej: Remera floreada talle M" />
        {errors.title && <p style={s.err}>{errors.title}</p>}
      </div>

      <div style={s.row3}>
        <div style={{flex:1}}>
          <label style={s.label}>Precio (ARS) *</label>
          <input style={{...s.input,...(errors.price?s.inputErr:{})}} type="number" min="0" value={form.price} onChange={e => set("price", e.target.value)} placeholder="0" />
          {errors.price && <p style={s.err}>{errors.price}</p>}
        </div>
        <div style={{flex:1}}>
          <label style={s.label}>Color/es</label>
          <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
            {COLORS.map(c => {
              const checked = form.color?.includes(c);
              return (
                <label key={c} style={{display:"flex",alignItems:"center",gap:6,padding:"4px 10px",borderRadius:20,fontSize:12,border:"1px solid #ddd",cursor:"pointer",background:checked?"#1D9E75":"#fff",color:checked?"#fff":"#555"}}>
                  <input type="checkbox" checked={checked} onChange={() => {
                    const current = form.color ? form.color.split(", ") : [];
                    const updated = checked ? current.filter(x => x !== c) : [...current, c];
                    const stock = { ...form.colorStock };
                    if (checked) { delete stock[c]; } else { stock[c] = ""; }
                    setForm(f => ({ ...f, color: updated.join(", "), colorStock: stock }));
                  }} />
                  {c}
                </label>
              );
            })}
          </div>
          <input style={{...s.input,marginTop:8}} placeholder="O escribí un color personalizado..."
            onBlur={e => {
              const val = e.target.value.trim();
              if (!val) return;
              const current = form.color ? form.color.split(", ") : [];
              setForm(f => ({ ...f, color: [...current, val].join(", "), colorStock: { ...f.colorStock, [val]: "" } }));
              e.target.value = "";
            }}
          />
          {form.color && <p style={{fontSize:12,color:"#1D9E75",marginTop:4}}>Seleccionado: {form.color}</p>}
          {colorList(form.color).length > 1 && (
            <div style={s.zoneCostsGrid}>
              {colorList(form.color).map(c => (
                <div key={c} style={s.zoneCostRow}>
                  <span style={s.zoneCostLabel}>{c}</span>
                  <input style={{...s.input,maxWidth:100}} type="number" min="0" value={form.colorStock[c] ?? ""}
                    onChange={e => setColorStock(c, e.target.value)} placeholder="Stock" />
                </div>
              ))}
            </div>
          )}
          {errors.colorStock && <p style={s.err}>{errors.colorStock}</p>}
        </div>
        <div style={{width:90}}>
          <label style={s.label}>Cantidad</label>
          {colorList(form.color).length > 1 ? (
            <div style={{...s.input,background:"#f5f5f5",color:"#555"}}>{totalFromColorStock(form.colorStock)}</div>
          ) : (
            <input style={s.input} type="number" min="0" value={form.quantity} onChange={e => set("quantity", Math.max(0, parseInt(e.target.value)||0))} />
          )}
        </div>
      </div>

      <div style={s.row2}>
        <div style={{flex:1}}>
          <label style={s.label}>Categoria</label>
          <select style={s.select} value={form.category} onChange={e => handleCategoryChange(e.target.value)}>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div style={{flex:1}}>
          <label style={s.label}>Zona</label>
          <select style={s.select} value={form.zone} onChange={e => set("zone", e.target.value)}>
            {ZONES.map(z => <option key={z} value={z}>{z}</option>)}
          </select>
        </div>
      </div>

      {extraFieldsDef.length > 0 && (
        <div style={s.extraSection}>
          <p style={s.extraTitle}>Detalles de {form.category}</p>
          <div style={s.extraGrid}>
            {extraFieldsDef.map(field => (
              <div key={field.key} style={s.extraField}>
                <label style={s.label}>{field.label}</label>
                {field.type === "select" ? (
                  <select style={s.select} value={form.extraFields[field.key] || ""} onChange={e => setExtra(field.key, e.target.value)}>
                    <option value="">Seleccionar...</option>
                    {field.options.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input style={s.input} value={form.extraFields[field.key] || ""} onChange={e => setExtra(field.key, e.target.value)} placeholder={field.placeholder} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={s.section}>
        <label style={s.label}>Descripcion (opcional)</label>
        <textarea style={s.textarea} rows={3} value={form.description} onChange={e => set("description", e.target.value)} placeholder="Contanos algo mas — por que lo vendes, detalles extra, etc. No hace falta repetir el material, talle o estado, eso ya lo completaste arriba." />
      </div>

      <div style={s.section}>
        <label style={s.label}>Opcion de envio</label>
        <div style={s.shippingRow}>
          {SHIPPING_OPTIONS.map(o => (
            <button key={o.value} style={{...s.shippingBtn,...(form.shipping===o.value?s.shippingBtnActive:{})}} onClick={() => set("shipping", o.value)}>
              {o.value==="no"?"🏠":o.value==="gratis"?"🎁":"📦"} {o.label}
            </button>
          ))}
        </div>

        {form.shipping === "no" && (
          <p style={s.shippingHelp}>El comprador va a tener que retirarlo en persona. No se pide direccion.</p>
        )}
        {form.shipping === "gratis" && (
          <p style={s.shippingHelp}>El comprador paga solo el precio del producto — el envio ya esta incluido ahi, vos te encargas de mandarlo.</p>
        )}
        {form.shipping === "comprador" && (
          <div style={{marginTop:10}}>
            <label style={s.label}>¿A que zonas enviás? Elegi las zonas y poneles un precio *</label>
            <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
              {ZONES.map(z => {
                const checked = z in form.shippingZoneCosts;
                return (
                  <label key={z} style={{display:"flex",alignItems:"center",gap:6,padding:"4px 10px",borderRadius:20,fontSize:12,border:"1px solid #ddd",cursor:"pointer",background:checked?"#1D9E75":"#fff",color:checked?"#fff":"#555"}}>
                    <input type="checkbox" checked={checked} onChange={() => toggleShippingZone(z)} />
                    {z}
                  </label>
                );
              })}
            </div>
            {Object.keys(form.shippingZoneCosts).length > 0 && (
              <div style={s.zoneCostsGrid}>
                {Object.keys(form.shippingZoneCosts).map(z => (
                  <div key={z} style={s.zoneCostRow}>
                    <span style={s.zoneCostLabel}>{z}</span>
                    <input style={{...s.input,maxWidth:140}} type="number" min="0" value={form.shippingZoneCosts[z]}
                      onChange={e => setShippingZoneCost(z, e.target.value)} placeholder="Costo de envio" />
                  </div>
                ))}
              </div>
            )}
            {errors.shippingCost && <p style={s.err}>{errors.shippingCost}</p>}
            <p style={s.shippingHelp}>El comprador va a elegir su zona antes de comprar y va a pagar el envio correspondiente junto con el producto, en el mismo pago de Mercado Pago. La plataforma no cobra comision sobre el envio, te llega completo a vos.</p>
          </div>
        )}

        {form.shipping !== "no" && (
          <div style={{marginTop:10}}>
            <label style={s.checkboxRow}>
              <input type="checkbox" checked={form.isInternational} onChange={e => set("isInternational", e.target.checked)} />
              🌎 Es un producto internacional / importado
            </label>

            <label style={{...s.label,marginTop:10}}>Tiempo estimado de entrega (opcional)</label>
            <input style={s.input} value={form.estimatedDelivery} onChange={e => set("estimatedDelivery", e.target.value)}
              placeholder={form.isInternational ? "Ej: 2 a 4 semanas" : "Ej: 3 a 5 dias"} />
            <p style={s.shippingHelp}>El comprador lo va a ver antes de comprar, para saber que esperar.</p>
          </div>
        )}
      </div>

      <div style={s.section}>
        <label style={s.label}>Medio de pago</label>
        <div style={s.mpBox}>💳 Mercado Pago</div>
        <p style={{fontSize:12,color:"#888",marginTop:6}}>Este marketplace solo acepta pagos con Mercado Pago.</p>
        {errors.pay && <p style={s.err}>{errors.pay}</p>}
      </div>

      {freeSalesLeft > 0 && (
        <div style={s.freeSalesBanner}>
          🎉 Tus primeras 2 ventas son gratis — te quedan {freeSalesLeft}. Después, 5% de comisión para el mantenimiento de la plataforma.
        </div>
      )}

      {priceNum > 0 && (
        <div style={s.commBox}>
          <p style={{fontWeight:600,marginBottom:10,color:"#0F6E56",fontSize:14}}>
            {effectiveRate === 0 ? "Desglose — venta sin comision 🎉" : "Desglose de comision (5%)"}
          </p>
          <div style={s.commRow}><span>Precio publicado</span><span>{formatPrice(priceNum)}</span></div>
          <div style={s.commRow}>
            <span>{effectiveRate === 0 ? "Comision plataforma" : "Comision plataforma (5%)"}</span>
            <span style={{color: effectiveRate === 0 ? "#1D9E75" : "#c0392b"}}>{effectiveRate === 0 ? "Gratis (primeras 2 ventas)" : `- ${formatPrice(commission)}`}</span>
          </div>
          <div style={{...s.commRow,fontWeight:600,borderTop:"0.5px solid #c9e8dc",paddingTop:8,marginTop:4}}>
            <span>Vos recibis</span><span style={{color:"#0F6E56",fontSize:16}}>{formatPrice(sellerReceives)}</span>
          </div>
        </div>
      )}

      <div style={s.btnRow}>
        <button style={s.btnCancel} onClick={() => navigate(-1)}>Cancelar</button>
        <button style={{...s.btnSubmit, opacity: uploading?0.6:1}} onClick={handleSubmit} disabled={uploading}>
          {uploading ? "Subiendo archivos..." : isEdit ? "Guardar cambios" : "Publicar producto"}
        </button>
      </div>
    </div>
  );
}

const s = {
  wrapper:{maxWidth:700,margin:"0 auto",padding:"32px 20px"},
  heading:{fontSize:22,fontWeight:600,marginBottom:24},
  section:{marginBottom:20},
  label:{display:"block",fontSize:13,color:"#555",marginBottom:6,fontWeight:500},
  input:{width:"100%",padding:"10px 12px",borderRadius:8,border:"0.5px solid #ddd",fontSize:14,color:"#111"},
  inputErr:{borderColor:"#E24B4A"},
  textarea:{width:"100%",padding:"10px 12px",borderRadius:8,border:"0.5px solid #ddd",fontSize:14,color:"#111",resize:"vertical"},
  select:{width:"100%",padding:"10px 12px",borderRadius:8,border:"0.5px solid #ddd",fontSize:14,color:"#111"},
  row3:{display:"flex",gap:12,marginBottom:20},
  row2:{display:"flex",gap:12,marginBottom:20},
  uploadZone:{display:"flex",flexDirection:"column",alignItems:"center",border:"1.5px dashed #b0dcc9",borderRadius:12,padding:"24px 20px",textAlign:"center",cursor:"pointer",marginBottom:12,background:"#fafffe"},
  thumbsRow:{display:"flex",gap:10,flexWrap:"wrap",marginTop:8},
  thumb:{position:"relative",width:80,height:80,flexShrink:0},
  thumbImg:{width:"100%",height:"100%",objectFit:"cover",borderRadius:8},
  thumbUploading:{width:"100%",height:"100%",background:"#1D9E75",borderRadius:8,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:4},
  thumbProgress:{width:"60%",height:4,background:"rgba(255,255,255,0.3)",borderRadius:2,overflow:"hidden"},
  thumbProgressFill:{height:"100%",background:"#fff",borderRadius:2,transition:"width 0.3s"},
  videoThumb:{position:"relative",width:"100%",height:"100%"},
  videoIcon:{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,background:"rgba(0,0,0,0.3)",borderRadius:8},
  removeThumb:{position:"absolute",top:-6,right:-6,width:20,height:20,borderRadius:"50%",background:"#E24B4A",color:"#fff",border:"none",cursor:"pointer",fontSize:10,display:"flex",alignItems:"center",justifyContent:"center"},
  extraSection:{background:"#f8fffe",border:"1px solid #c9e8dc",borderRadius:12,padding:"16px 20px",marginBottom:20},
  extraTitle:{fontWeight:600,color:"#0F6E56",fontSize:14,marginBottom:14},
  extraGrid:{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14},
  extraField:{},
  shippingRow:{display:"flex",gap:8,flexWrap:"wrap"},
  zoneCostsGrid:{display:"flex",flexDirection:"column",gap:8,marginTop:12},
  zoneCostRow:{display:"flex",alignItems:"center",gap:10},
  zoneCostLabel:{fontSize:13,color:"#333",minWidth:120},
  shippingHelp:{fontSize:12,color:"#888",marginTop:8,lineHeight:1.5},
  checkboxRow:{display:"flex",alignItems:"center",gap:8,fontSize:13,color:"#333",cursor:"pointer"},
  shippingBtn:{padding:"9px 14px",borderRadius:8,border:"0.5px solid #ddd",background:"#fff",fontSize:13,cursor:"pointer",color:"#555"},
  shippingBtnActive:{background:"#fff8e1",borderColor:"#f0c040",color:"#b7830a",fontWeight:500},
  mpBox:{background:"#E1F5EE",border:"0.5px solid #b0dcc9",borderRadius:8,padding:"10px 14px",fontSize:14,color:"#0F6E56",fontWeight:500},
  commBox:{background:"#f0faf6",border:"0.5px solid #b0dcc9",borderRadius:10,padding:"14px 16px",marginBottom:24,fontSize:13},
  freeSalesBanner:{background:"#1D9E75",color:"#fff",borderRadius:10,padding:"14px 16px",marginBottom:16,fontSize:14,fontWeight:500,lineHeight:1.5},
  commRow:{display:"flex",justifyContent:"space-between",color:"#444",marginBottom:4},
  err:{color:"#E24B4A",fontSize:12,marginTop:4},
  btnRow:{display:"flex",gap:10,justifyContent:"flex-end"},
  btnCancel:{padding:"10px 20px",border:"0.5px solid #ddd",borderRadius:8,background:"#fff",cursor:"pointer",fontSize:14,color:"#555"},
  btnSubmit:{padding:"10px 28px",background:"#1D9E75",color:"#fff",border:"none",borderRadius:8,fontSize:14,fontWeight:500,cursor:"pointer"},
};
